/**
 * @typedef {Object} SegmentCriteria
 * @property {string} id - Unique criteria identifier
 * @property {string} field - Field name to evaluate (e.g., 'status', 'leadScore', 'location')
 * @property {'equals'|'contains'|'greater_than'|'less_than'|'in'|'not_in'|'between'} operator - Comparison operator
 * @property {any} value - Value to compare against
 * @property {number} weight - Criteria weight for scoring (0-1)
 * @property {'AND'|'OR'} logicalOperator - Logical operator for chaining criteria
 */

/**
 * @typedef {Object} SegmentPreviewData
 * @property {number} estimatedSize - Estimated number of leads matching criteria
 * @property {Object[]} sampleProfiles - Sample leads that match the criteria
 * @property {number} qualityScore - Segment quality score (0-100)
 * @property {string[]} recommendations - Suggestions for improving the segment
 */

/**
 * @typedef {Object} AudienceSegment
 * @property {string} id - Unique segment identifier
 * @property {string} name - Segment display name
 * @property {string} description - Optional segment description
 * @property {SegmentCriteria[]} criteria - Array of criteria defining the segment
 * @property {number} size - Current number of leads in segment
 * @property {number} estimatedSize - Estimated size during preview
 * @property {string[]} tags - Array of tags for categorization
 * @property {Date} createdAt - Creation timestamp
 * @property {Date} updatedAt - Last update timestamp
 * @property {'user'|'ai'} createdBy - How the segment was created
 * @property {number} usageCount - Number of times segment has been used
 * @property {Date} lastUsed - Last usage timestamp
 * @property {Object} expectedPerformance - Expected performance metrics
 * @property {number} expectedPerformance.openRate - Expected email open rate
 * @property {number} expectedPerformance.clickRate - Expected click rate
 * @property {number} expectedPerformance.conversionRate - Expected conversion rate
 * @property {'active'|'inactive'|'draft'} status - Segment status
 */

/**
 * Available fields for segment criteria based on leads data structure
 */
export const SEGMENT_FIELDS = {
  basic_info: [
    { 
      key: 'name', 
      label: 'Name', 
      type: 'text', 
      operators: ['equals', 'contains'],
      description: 'Lead\'s full name'
    },
    { 
      key: 'email', 
      label: 'Email', 
      type: 'text', 
      operators: ['equals', 'contains'],
      description: 'Lead\'s email address'
    },
    { 
      key: 'phone', 
      label: 'Phone', 
      type: 'text', 
      operators: ['equals', 'contains'],
      description: 'Lead\'s phone number'
    },
    { 
      key: 'location', 
      label: 'Location', 
      type: 'select', 
      operators: ['equals', 'contains', 'in', 'not_in'],
      description: 'Lead\'s geographic location'
    }
  ],
  lead_qualification: [
    { 
      key: 'status', 
      label: 'Lead Status', 
      type: 'select', 
      operators: ['equals', 'in', 'not_in'],
      options: ['hot', 'warm', 'cold'],
      description: 'Current qualification status of the lead'
    },
    { 
      key: 'leadScore', 
      label: 'Lead Score', 
      type: 'number', 
      operators: ['equals', 'greater_than', 'less_than', 'between'],
      description: 'Numerical score indicating lead quality (0-100)'
    },
    { 
      key: 'source', 
      label: 'Lead Source', 
      type: 'select', 
      operators: ['equals', 'in', 'not_in'],
      options: ['Website Form', 'Social Media', 'Referral', 'Direct', 'Advertisement'],
      description: 'How the lead was acquired'
    },
    { 
      key: 'aiSegment', 
      label: 'AI Segment', 
      type: 'select', 
      operators: ['equals', 'contains', 'in', 'not_in'],
      description: 'AI-generated segment classification'
    }
  ],
  engagement: [
    { 
      key: 'lastEngaged', 
      label: 'Last Engaged', 
      type: 'date', 
      operators: ['equals', 'greater_than', 'less_than', 'between'],
      description: 'Date of last interaction with the lead'
    },
    { 
      key: 'tags', 
      label: 'Tags', 
      type: 'multi-select', 
      operators: ['contains', 'in', 'not_in'],
      description: 'Custom tags assigned to the lead'
    }
  ]
};

/**
 * Operator definitions with human-readable labels
 */
export const OPERATORS = {
  equals: { label: 'equals', description: 'Exactly matches' },
  contains: { label: 'contains', description: 'Contains text' },
  greater_than: { label: 'greater than', description: 'Is greater than' },
  less_than: { label: 'less than', description: 'Is less than' },
  between: { label: 'between', description: 'Is between two values' },
  in: { label: 'is one of', description: 'Matches any of the selected values' },
  not_in: { label: 'is not one of', description: 'Does not match any of the selected values' }
};

/**
 * Logical operators for chaining criteria
 */
export const LOGICAL_OPERATORS = {
  AND: { label: 'AND', description: 'All conditions must be true' },
  OR: { label: 'OR', description: 'At least one condition must be true' }
};